import { sectionRegistry } from '../canvas/sectionRegistry';
import { bt } from '../i18n';
import { isReadOnlyView } from '../boot/view-handoff';
import { useBuilderStore } from '../state/builderStore';
import { sectionTypeIcon } from '../ui/icons';
import { AddPopover, type AddPopoverGroup } from './AddPopover';
import { SectionPreview } from './SectionPreview';
import { sectionTypeName } from './sectionVariantLabel';

/**
 * The "Add section" picker (build-plan §7.4 / §11.2) — a Shopify-style FLOATING picker (chrome feature 3):
 * an in-app popover (NO native popup) with a search field and a CATEGORIZED list, each row showing the
 * section's type icon + name. No "Apps" tab: no marketplace app ships sections, so it was an empty state. Sourced from the registry by `def.meta` (label/group/icon),
 * grouped by `meta.group`; the search filters by label across groups.
 *
 * The registry ships to the STOREFRONT, so its `meta` stays English and the seller's language is put on
 * here, at the seam — same contract as the rail (`sectionDisplayName`). Until 13/09 this component read
 * `meta.label`/`meta.group` raw: every `secname.*` phrase already existed, the picker simply never asked
 * for them, so a French rail opened an English picker and typing "produit" matched nothing. The search
 * filters the TRANSLATED label for that reason.
 *
 * The picker UI + floating/anchored/dismiss mechanics live in the shared {@link AddPopover} (rendered in a
 * portal so it floats over the canvas instead of being clipped by the rail's `overflow:hidden`). This
 * component only maps the registry → items and dispatches the unchanged `insertSection(type, atIndex)`
 * store action (stable nanoid id, selects the new section). `atIndex` is optional: the rail's bottom
 * button appends; the per-gap inline adder passes a gap index.
 */
export function AddSectionMenu({
  atIndex,
  label,
  className = 'bld-add',
  triggerAriaLabel,
  align,
  filter,
}: {
  atIndex?: number;
  label?: string;
  className?: string;
  /** Accessible name when the trigger is icon-only (e.g. the canvas circular "+" affordance). */
  triggerAriaLabel?: string;
  /** Anchor alignment for the floating panel (the canvas '+' centres under its tiny button). */
  align?: 'start' | 'center';
  /** Restrict which section types are offered — e.g. the Header group only offers chrome sections, the
   *  Template picker excludes them. Omitted = all registry sections. */
  filter?: (type: string) => boolean;
}) {
  const insertSection = useBuilderStore((s) => s.insertSection);

  // Group the registry sections by meta.group, preserving registry order within each group. The map is
  // keyed on the ENGLISH group so registry order survives; the heading is translated once, at the end.
  const byGroup = new Map<string, AddPopoverGroup['items']>();
  for (const def of Object.values(sectionRegistry)) {
    if (filter && !filter(def.type)) continue;
    const arr = byGroup.get(def.meta.group) ?? [];
    arr.push({
      type: def.type,
      label: sectionTypeName(def.type, def.meta.label),
      // The English name and the type id, so the search finds a section by the word the seller knows.
      keywords: `${def.meta.label} ${def.type}`,
      icon: sectionTypeIcon(def.meta.icon, 16),
      previewIcon: sectionTypeIcon(def.meta.icon, 28),
    });
    byGroup.set(def.meta.group, arr);
  }
  const groups: AddPopoverGroup[] = [...byGroup.entries()].map(([group, items]) => ({
    group: bt(`chrome.seccat.${group.toLowerCase().replace(/ /g, '_')}`, group),
    items,
  }));

  // The visible/accessible text is the label sans any leading "+ " (the circled-plus icon carries the
  // affordance now, Shopify-style). Default trigger reads "Add section".
  const fallback = bt('chrome.rail.add_section', '+ Add section');
  const effective = label ?? fallback;
  const triggerLabel =
    className === 'bld-add' ? fallback.replace(/^\+\s*/, '') : effective.replace(/^\+\s*/, '');

  // Superadmin consultation: every add-section affordance (rail, inline inserts, canvas "+") comes
  // through here, so one greyed trigger covers them all. Server + http lock stay the real guarantee.
  const readOnly = isReadOnlyView();

  return (
    <AddPopover
      groups={groups}
      onChoose={(type) => insertSection(type, atIndex)}
      triggerLabel={triggerLabel}
      triggerClassName={className}
      triggerAriaLabel={triggerAriaLabel}
      triggerDisabled={readOnly}
      triggerTitle={readOnly ? bt('chrome.view.readonly', 'Read-only consultation') : undefined}
      searchPlaceholder={bt('chrome.rail.search_sections', 'Search sections')}
      menuAriaLabel={bt('chrome.rail.add_here', 'Add a section here')}
      emptyText={bt('chrome.rail.no_section_match', 'No sections match “{q}”.')}
      twoPane
      // A live, scaled render of the section's default preset (Shopify-parity preview-before-add). Keyed on
      // the type so switching sections remounts with a clean measurement instead of a one-frame stale height.
      renderPreview={(type) => <SectionPreview key={type} type={type} />}
      align={align}
    />
  );
}
